import { Minus, Plus } from "@assets";
import { Row } from "@components/Flex";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, Title2 } from "@components/Typography";
import { DEFAULT_DEPOSIT_ADJUSTMENT, MAX_DEPOSIT_AMOUNT } from "@constants";
import { Flow, SipFrequency } from "@ctypes";
import { useGoalFlow, useProjectedChart } from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  formatCurrency,
  getAvailableFrequencies,
  getMinDepositAmount,
  getRecurringTypeLabel,
} from "@utils";
import { useStyles } from "./styles";

const ALL_DEPOSIT_FREQUENCIES = [SipFrequency.Weekly, SipFrequency.Monthly];

export const DepositAdjuster = () => {
  const styles = useStyles();

  const {
    accountType,
    recurringAmount,
    setRecurringAmount,
    recurringDepositType,
    setRecurringDepositType,
  } = useProjectedChart();

  const {
    goalProps: { flow },
  } = useGoalFlow();

  const recurringTypeOptions = getAvailableFrequencies(ALL_DEPOSIT_FREQUENCIES, accountType);

  const minDepositAmount = getMinDepositAmount(accountType);

  const isAcceptGoalFlow = flow === Flow.AcceptSharedGoal;

  const canDecrement =
    recurringAmount > minDepositAmount || (flow === Flow.EditGoal && recurringAmount > 0);

  const increment = () => {
    if (recurringAmount + DEFAULT_DEPOSIT_ADJUSTMENT < MAX_DEPOSIT_AMOUNT) {
      setRecurringAmount(recurringAmount + DEFAULT_DEPOSIT_ADJUSTMENT);
    } else {
      setRecurringAmount(MAX_DEPOSIT_AMOUNT);
    }
  };

  const decrement = () => {
    if (recurringAmount - DEFAULT_DEPOSIT_ADJUSTMENT >= minDepositAmount) {
      setRecurringAmount(recurringAmount - DEFAULT_DEPOSIT_ADJUSTMENT);
    } else if (flow === Flow.EditGoal) {
      setRecurringAmount(0);
    } else {
      setRecurringAmount(minDepositAmount);
    }
  };

  const selectFrequency = (frequency: SipFrequency) => {
    if (isAcceptGoalFlow) return;
    setRecurringDepositType(frequency);
  };

  const hasMultipleFrequencies = recurringTypeOptions.length > 1;

  const amount = formatCurrency(recurringAmount);
  const frequencyLabel = getRecurringTypeLabel(recurringDepositType);

  return (
    <div style={styles.container}>
      {hasMultipleFrequencies && !isAcceptGoalFlow ? (
        <Row style={styles.inner}>
          <div style={styles.segmentedContainer}>
            {recurringTypeOptions.map((option) => {
              const active = option === recurringDepositType;
              return (
                <div
                  key={option}
                  style={{
                    ...styles.segment,
                    ...(active ? styles.segmentActive : {}),
                  }}
                  onClick={() => selectFrequency(option)}
                >
                  <Body style={active ? styles.segmentActiveText : styles.segmentText}>
                    {getRecurringTypeLabel(option)}
                  </Body>
                </div>
              );
            })}
          </div>
        </Row>
      ) : (
        <Row style={styles.inner}>
          <Body style={styles.left}>{frequencyLabel}</Body>
          <Body style={styles.right}>{i18n.t(Translations.ProjectedChartRecurringAmount)}</Body>
        </Row>
      )}

      <Spacing size={SpacingSizes.M} />

      <Row style={styles.counterContainer}>
        <div
          style={{
            ...styles.circle,
            ...(!canDecrement ? styles.circleDisabled : {}),
          }}
          onClick={() => {
            if (canDecrement) decrement();
          }}
        >
          <Minus />
        </div>
        <Title2 style={styles.amount}>{amount}</Title2>
        <div style={styles.circle} onClick={increment}>
          <Plus />
        </div>
      </Row>
      <Spacing size={SpacingSizes.XXS} />
    </div>
  );
};
